import { useEffect, useMemo, useRef, useState } from 'react';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useTranslation } from 'react-i18next';
import { ProductReservation } from '_api/useReservations';
import { titleFontSize, screenMargin, smallestMargin, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CheckBox, HeimeText, PrimaryButton } from 'Components';
import BottomSheet from 'Components/Modal/Bottomsheet';
import { Product } from 'types/Category';
import { UnixTimeCode } from 'types/Utility';
import isSlotTaken from './isSlotTaken';

const SelectInstanceModal = ({
    onClose,
    onComplete,
    instances,
    nToSelect,
    start_at,
    end_at,
    bookings,
    initialInstances,
}: {
    onClose: () => void;
    onComplete: (instances: string[]) => void;
    instances: Product['product_instances'];
    bookings: Array<ProductReservation>;
    nToSelect: number;
    start_at: UnixTimeCode;
    end_at: UnixTimeCode;
    initialInstances: Array<string>;
}) => {
    const bottomSheetRef = useRef<BottomSheetModal>(null);
    const { theme } = useThemeContext();
    const { t } = useTranslation();
    const [selectedInstances, setSelectedInstances] = useState<Array<string>>(initialInstances);

    useEffect(() => {
        bottomSheetRef.current?.present();
    }, []);

    const handleChange = (index: number) => {
        if (index === -1) {
            onClose();
        }
    };

    const isValid = selectedInstances.length <= nToSelect && selectedInstances.length > 0;

    const mappedInstances = useMemo(() => {
        return instances.map((instance) => {
            return {
                ...instance,
                selectable: !isSlotTaken({
                    start: start_at,
                    end: end_at,
                    bookings: bookings.filter((booking) =>
                        booking.product_instances.map((i) => i.id).includes(instance.id),
                    ),
                    amount: 1,
                    capacity: 1,
                }),
            };
        });
    }, [instances, start_at, end_at, bookings]);

    return (
        <BottomSheet
            onChange={handleChange}
            ref={bottomSheetRef}
            enableDynamicSizing
            content={
                <>
                    {mappedInstances.map((instance) => {
                        const isSelected = selectedInstances.includes(instance.id);
                        const toggle = () => {
                            if (isSelected) {
                                setSelectedInstances((prev) => prev.filter((i) => i !== instance.id));
                            } else {
                                setSelectedInstances((prev) => {
                                    if (prev.length < nToSelect) {
                                        return [...prev, instance.id];
                                    }
                                    const [, ...rest] = prev;
                                    return [...rest, instance.id];
                                });
                            }
                        };
                        return (
                            <CheckBox
                                key={instance.id}
                                checked={isSelected}
                                disabled={!instance.selectable}
                                onPress={toggle}
                                style={{ marginLeft: smallestMargin }}
                                title={instance.label}
                                endContent={
                                    instance.selectable ? null : (
                                        <HeimeText style={{ color: theme.red, fontSize: subtitleFontSize }}>
                                            {t('serviceExpanded:notAvailable')}
                                        </HeimeText>
                                    )
                                }
                                titleStyle={{
                                    fontSize: titleFontSize,
                                    width: '100%',
                                    flex: 1,
                                    marginLeft: screenMargin,
                                }}
                            />
                        );
                    })}
                </>
            }
            bottom={
                <PrimaryButton
                    text={
                        selectedInstances.length === 0
                            ? t('serviceExpanded:select_instance')
                            : selectedInstances.length > nToSelect
                              ? t('serviceExpanded:too_many_instances')
                              : t('serviceExpanded:confirm_instances')
                    }
                    onPress={() => onComplete(selectedInstances)}
                    status={isValid ? null : 'disabled'}
                />
            }
        />
    );
};

export default SelectInstanceModal;
